import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { reportApi } from '../lib/api';
import { CheckCircleIcon, XCircleIcon, ClockIcon, ArrowDownTrayIcon, TableCellsIcon, PaperAirplaneIcon, PencilIcon, DocumentTextIcon, ExclamationTriangleIcon, ChevronLeftIcon, ChevronRightIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import toast from 'react-hot-toast';
import LockedFeatureButton from '../components/LockedFeatureButton';
import BulkSubmitModal from '../components/BulkSubmitModal';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PASSED', label: 'No Issues' },
  { value: 'FAILED', label: 'Flagged' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ERROR', label: 'Error' },
];

function ApprovalBadge({ approval }: { approval?: { status: string } }) {
  if (!approval) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
        Not Submitted
      </span>
    );
  }

  switch (approval.status) {
    case 'DRAFT':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <PencilIcon className="h-3.5 w-3.5 mr-1" />
          Draft
        </span>
      );
    case 'PENDING_REVIEW':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <ClockIcon className="h-3.5 w-3.5 mr-1" />
          In Review
        </span>
      );
    case 'APPROVED':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
          Approved
        </span>
      );
    case 'REJECTED':
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="h-3.5 w-3.5 mr-1" />
          Needs Revision
        </span>
      );
    default:
      return null;
  }
}

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExportingXlsx, setIsExportingXlsx] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const statusFilter = searchParams.get('status') || '';
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['reports', currentPage, statusFilter],
    queryFn: async () => {
      const response = await reportApi.getReports({
        page: currentPage,
        limit: pageSize,
        ...(statusFilter && { status: statusFilter }),
      });
      return response.data;
    },
  });

  const pagination = data?.pagination;
  const totalPages = pagination?.pages || 1;

  const setPage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    setSearchParams(params);
    setSelectedReports([]);
  };

  const setStatus = (status: string) => {
    const params = new URLSearchParams(searchParams);
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    params.set('page', '1');
    setSearchParams(params);
    setSelectedReports([]);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const completedReportIds = data?.reports
        ?.filter((r: any) => r.status === 'PASSED' || r.status === 'FAILED')
        .map((r: any) => r.id) || [];
      setSelectedReports(completedReportIds);
    } else {
      setSelectedReports([]);
    }
  };

  const handleSelectReport = (reportId: string) => {
    setSelectedReports(prev =>
      prev.includes(reportId)
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleBulkDownload = async () => {
    if (selectedReports.length === 0) {
      toast.error('Please select at least one report to download');
      return;
    }

    setIsDownloading(true);
    try {
      const response = await reportApi.downloadBulkReports(selectedReports);

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `compliance_reports_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${selectedReports.length} report(s) successfully`);
      setSelectedReports([]);
    } catch (error) {
      toast.error('Unable to download reports. Please try again.');
      console.error('Bulk download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleExportExaminerXlsx = async () => {
    setIsExportingXlsx(true);
    try {
      const response = await reportApi.downloadExaminerXlsx();

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `examiner_marketing_outreach_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Examiner spreadsheet exported successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Unable to export spreadsheet. Please try again.');
      console.error('XLSX export error:', error);
    } finally {
      setIsExportingXlsx(false);
    }
  };

  const completedReports = data?.reports?.filter((r: any) =>
    r.status === 'PASSED' || r.status === 'FAILED'
  ) || [];
  const allSelected = completedReports.length > 0 && selectedReports.length === completedReports.length;

  // Check if any selected reports are submittable (no approval, DRAFT, or REJECTED)
  const submittableSelected = selectedReports.filter(id => {
    const report = data?.reports?.find((r: any) => r.id === id);
    if (!report) return false;
    const approvalStatus = report.approvals?.[0]?.status;
    return !approvalStatus || approvalStatus === 'DRAFT' || approvalStatus === 'REJECTED';
  });

  // Get selected report details for the modal
  const selectedReportDetails = selectedReports
    .map(id => data?.reports?.find((r: any) => r.id === id))
    .filter(Boolean);

  // Find teamId from selected reports (use the first one that has a team)
  const teamId = selectedReportDetails.find((r: any) => r?.teamId)?.teamId;

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Compliance Reports</h1>
          <p className="mt-2 text-gray-600">
            View all your compliance check results
            {pagination && ` (${pagination.total} total)`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <LockedFeatureButton
            feature="examiner_xlsx"
            onClick={handleExportExaminerXlsx}
            variant="secondary"
            disabled={isExportingXlsx}
          >
            <TableCellsIcon className="h-5 w-5 mr-2" />
            {isExportingXlsx ? 'Exporting...' : 'Examiner Spreadsheet'}
          </LockedFeatureButton>

          {selectedReports.length > 0 && (
            <>
              {submittableSelected.length > 0 && (
                <button
                  onClick={() => setShowSubmitModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-all shadow-sm hover:shadow-md"
                >
                  <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                  Submit {submittableSelected.length} for Review
                </button>
              )}

              <button
                onClick={handleBulkDownload}
                disabled={isDownloading}
                className="inline-flex items-center px-4 py-2 bg-ocean-600 text-white rounded-md hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                {isDownloading ? 'Downloading...' : `Download ${selectedReports.length}`}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="mb-4 flex items-center gap-3">
        <FunnelIcon className="h-5 w-5 text-gray-400" />
        <div className="flex gap-1.5">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatus(opt.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                statusFilter === opt.value
                  ? 'bg-ocean-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compliance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Approval
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.reports?.map((report: any) => {
                const isCompleted = report.status === 'PASSED' || report.status === 'FAILED';
                const isSelected = selectedReports.includes(report.id);

                return (
                  <tr key={report.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectReport(report.id)}
                        disabled={!isCompleted}
                        className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {report.upload?.fileName || 'Untitled'}
                      </div>
                      <div className="text-sm text-gray-500">{report.upload?.fileType || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {report.status === 'PASSED' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          No Issues
                        </span>
                      )}
                      {report.status === 'FAILED' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                          Flagged for Review
                        </span>
                      )}
                      {(report.status === 'PENDING' || report.status === 'PROCESSING') && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {report.status === 'PROCESSING' ? 'Processing' : 'Pending'}
                        </span>
                      )}
                      {report.status === 'ERROR' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Error
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <ApprovalBadge approval={report.approvals?.[0]} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/reports/${report.id}`}
                        className="text-primary-600 hover:text-primary-900 font-medium"
                      >
                        View Report
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isLoading && (
            <div className="divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse flex items-center px-6 py-4 space-x-6">
                  <div className="h-4 w-4 bg-gray-200 rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-48 bg-gray-200 rounded" />
                    <div className="h-3 w-24 bg-gray-100 rounded" />
                  </div>
                  <div className="h-5 w-20 bg-gray-200 rounded-full" />
                  <div className="h-5 w-24 bg-gray-200 rounded-full" />
                  <div className="h-4 w-20 bg-gray-200 rounded" />
                  <div className="h-4 w-16 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          )}
          {!isLoading && (!data || data.reports.length === 0) && (
            <div className="text-center py-16">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-3 text-sm font-medium text-gray-900">
                {statusFilter ? 'No matching reports' : 'No reports yet'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter
                  ? 'Try adjusting your filter to see more reports.'
                  : 'Upload your first ad to get started with compliance checking.'}
              </p>
              {statusFilter ? (
                <button
                  onClick={() => setStatus('')}
                  className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Clear filter
                </button>
              ) : (
                <div className="mt-4">
                  <Link to="/upload" className="text-sm font-medium text-primary-600 hover:text-primary-500">
                    Upload an ad &rarr;
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1]) > 1) acc.push('ellipsis');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === 'ellipsis' ? (
                    <span key={`e-${idx}`} className="px-1 text-gray-400">...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setPage(item)}
                      className={`px-3 py-1.5 text-sm rounded-md ${
                        currentPage === item
                          ? 'bg-ocean-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {showSubmitModal && (
        <BulkSubmitModal
          reportIds={submittableSelected}
          reports={selectedReportDetails}
          teamId={teamId}
          onClose={() => setShowSubmitModal(false)}
          onSuccess={() => {
            setSelectedReports([]);
            setShowSubmitModal(false);
          }}
        />
      )}
    </div>
  );
}
